import apiClient from '@/lib/api-client'
import type {
  AddLearnedSkillsRequest,
  AddLearnedSkillsResponse,
  Document,
  DocumentDetail,
  DocumentRevision,
  DocumentUploadUrlRequest,
  DocumentUploadUrlResponse,
  JobPosting,
  JobPostingCreate,
  JobPostingList,
  ResumeMatchList,
  ResumeMatchRequest,
  ResumeMatchResponse,
  RevisionDownloadResponse,
  RevisionGenerateRequest,
} from '@/types'

export const documentService = {
  async getUploadUrl(request: DocumentUploadUrlRequest): Promise<DocumentUploadUrlResponse> {
    const response = await apiClient.post<DocumentUploadUrlResponse>(
      '/api/v1/documents/upload-url',
      request,
    )
    return response.data
  },

  async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      // Handle abort signal
      if (signal) {
        signal.addEventListener('abort', () => {
          xhr.abort()
          reject(new DOMException('Upload cancelled', 'AbortError'))
        })
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`))
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('S3 upload failed: Network error'))
      })

      xhr.addEventListener('abort', () => {
        reject(new DOMException('Upload cancelled', 'AbortError'))
      })

      xhr.open('PUT', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  },

  async deleteDocument(documentId: number): Promise<void> {
    await apiClient.delete(`/api/v1/documents/${documentId}`)
  },

  async startProcessing(documentId: number): Promise<Document> {
    const response = await apiClient.post<Document>(`/api/v1/documents/${documentId}/process`)
    return response.data
  },

  async getDocument(documentId: number): Promise<DocumentDetail> {
    const response = await apiClient.get<DocumentDetail>(`/api/v1/documents/${documentId}`)
    return response.data
  },

  async listDocuments(): Promise<Document[]> {
    const response = await apiClient.get<Document[]>('/api/v1/documents')
    return response.data
  },

  async getRevisionDownloadUrl(
    documentId: number,
    revisionId: number,
    format: 'pdf' | 'docx' = 'pdf',
    filename?: string,
  ): Promise<RevisionDownloadResponse> {
    const response = await apiClient.get<RevisionDownloadResponse>(
      `/api/v1/documents/${documentId}/revisions/${revisionId}/download`,
      { params: { format, ...(filename ? { filename } : {}) } },
    )
    return response.data
  },

  async getOriginalDownloadUrl(documentId: number): Promise<RevisionDownloadResponse> {
    const response = await apiClient.get<RevisionDownloadResponse>(
      `/api/v1/documents/${documentId}/download`,
    )
    return response.data
  },

  async generateRevision(
    documentId: number,
    request: RevisionGenerateRequest = {},
  ): Promise<DocumentRevision> {
    const response = await apiClient.post<DocumentRevision>(
      `/api/v1/documents/${documentId}/generate-revision`,
      request,
    )
    return response.data
  },

  // Job Posting methods
  async createJobPosting(request: JobPostingCreate): Promise<JobPosting> {
    const response = await apiClient.post<JobPosting>('/api/v1/job-postings', request)
    return response.data
  },

  async listJobPostings(): Promise<JobPostingList> {
    const response = await apiClient.get<JobPostingList>('/api/v1/job-postings')
    return response.data
  },

  async getJobPosting(jobPostingId: number): Promise<JobPosting> {
    const response = await apiClient.get<JobPosting>(`/api/v1/job-postings/${jobPostingId}`)
    return response.data
  },

  // Match methods
  async analyzeMatch(
    documentId: number,
    request: ResumeMatchRequest,
  ): Promise<ResumeMatchResponse> {
    const response = await apiClient.post<ResumeMatchResponse>(
      `/api/v1/documents/${documentId}/match`,
      request,
    )
    return response.data
  },

  async listDocumentMatches(documentId: number): Promise<ResumeMatchList> {
    const response = await apiClient.get<ResumeMatchList>(`/api/v1/documents/${documentId}/matches`)
    return response.data
  },

  async addLearnedSkills(
    documentId: number,
    payload: AddLearnedSkillsRequest,
  ): Promise<AddLearnedSkillsResponse> {
    const response = await apiClient.post<AddLearnedSkillsResponse>(
      `/api/v1/documents/${documentId}/add-learned-skills`,
      payload,
    )
    return response.data
  },
}
